import React, {useEffect, useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {XpertKnowledgeSubSectionDto} from '../../../services/XpertKnowledgeSection/type'
import {ReactComponent as DownArrowIcon} from '../../../common/assets/down-arrow-icon.svg'
import {ReactComponent as CloseIcon} from '../../../common/assets/close-icon.svg'
import {motion} from 'framer-motion'
import {XpertKnowledgeDto} from '../../../services/XpertKnowledge/type'
import {XpertKnowledgeTutorialDto, XpertKnowledgeTutorialStatus} from '../../../services/XpertKnowledgeTutorial/type'
import {useNavigate} from 'react-router-dom'
import {useResponsiveBreakpoints} from '../../../hooks/mediaQueries'
import i18n from '../../../common/i18n'
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown'

interface SidebarProps {
  selectedSectionId: number | undefined
  setSelectedSectionId: (id: number) => void
  selectedSubSectionId?: number
  setSelectedSubSectionId: (id?: number) => void
  selectedXpertKnowledge?: XpertKnowledgeDto | undefined
  selectedXpertKnowledgeTutorial: XpertKnowledgeTutorialDto
  setSelectedXpertKnowledgeTutorial?: (xkTutorial: XpertKnowledgeTutorialDto) => void
  xpertKnowledgeTutorials?: XpertKnowledgeTutorialDto[]
  onSectionSelect?: () => void
}

const XpertKnowledgeTutorialSidebar: React.FC<SidebarProps> = ({
  selectedSectionId,
  setSelectedSectionId,
  selectedSubSectionId,
  setSelectedSubSectionId,
  selectedXpertKnowledge,
  selectedXpertKnowledgeTutorial,
  setSelectedXpertKnowledgeTutorial,
  xpertKnowledgeTutorials,
  onSectionSelect,
}) => {
  const navigate = useNavigate()
  const {tablet, laptop} = useResponsiveBreakpoints()

  const [openSections, setOpenSections] = useState<Record<number, boolean>>({[selectedSectionId || 0]: true})

  useEffect(() => {
    if (selectedSectionId) {
      setOpenSections((prev) => ({...prev, [selectedSectionId]: true}))
    }
  }, [selectedSectionId])

  const toggleSection = (id: number) => {
    setOpenSections((prev) => ({...prev, [id]: !prev[id]}))
  }

  const versionOptions: string[] = (xpertKnowledgeTutorials ?? []).map((tutorial) => {
    const baseOption = `Version ${tutorial.version}`
    if (selectedXpertKnowledge?.publishTutorial?.id && selectedXpertKnowledge.publishTutorial.id === tutorial.id) {
      return `${baseOption} (Published)`
    }
    return baseOption
  })

  const handleVersionChange = (selectedValue: string) => {
    const extractedVersion = parseInt(selectedValue.replace(/\D/g, ''), 10)
    const selectedTutorial = xpertKnowledgeTutorials?.find((tut) => tut.version === extractedVersion)

    if (selectedTutorial && setSelectedXpertKnowledgeTutorial && selectedXpertKnowledgeTutorial.id !== selectedTutorial.id) {
      setSelectedXpertKnowledgeTutorial(selectedTutorial)
    }
  }

  const handleSectionClick = (sectionId: number, subSectionId?: number) => {
    setSelectedSectionId(sectionId)
    setSelectedSubSectionId(subSectionId)
    navigate(subSectionId ? `#section-${sectionId}-subsection-${subSectionId}` : `#section-${sectionId}`)
    onSectionSelect?.()
  }

  return (
    <Col
      backgroundColor="#F7F8F9"
      height="100%"
      overflowY="scroll"
      boxSizing="border-box"
      width="329px"
      minWidth="240px"
      transition="all 0.3s ease"
      {...(!laptop && {
        width: '330px',
        minWidth: '330px',
      })}
    >
      {!laptop && (
        <Row
          padding="20px 16px"
          borderBottom="1px solid #DADCE0"
          backgroundColor="#ECEEF3"
          alignItems="center"
          justifyContent="space-between"
          transition="all 0.3s ease"
        >
          <Block fontSize="18px" fontWeight={500} lineHeight="23px">
            {i18n.t('xk.tutorial.sidebar.title')}
          </Block>
          <Block
            cursor="pointer"
            transition="all 0.2s ease"
            props={{
              onClick: onSectionSelect,
            }}
            marginTop="4px"
          >
            <CloseIcon width={16} height={16} color="black" />
          </Block>
        </Row>
      )}

      <Col padding="12px 12px" gap="4px" width="100%">
        <Block fontWeight={500} color="#100E11" fontSize="14px">
          Version
        </Block>
        <CustomDropdown
          options={[...versionOptions].sort((a, b) => parseInt(b.replace(/\D/g, ''), 10) - parseInt(a.replace(/\D/g, ''), 10))}
          value={
            selectedXpertKnowledgeTutorial
              ? `V.${selectedXpertKnowledgeTutorial.version}${
                  selectedXpertKnowledgeTutorial?.status == XpertKnowledgeTutorialStatus.PUBLISHED ? ' (Published)' : ''
                }`
              : 'Select Version'
          }
          onChange={handleVersionChange}
          width="90%"
          borderColor="#5F5073"
          hoverBackgroundColor="#ECEEF3"
        />
      </Col>

      {selectedXpertKnowledgeTutorial.sections
        ?.sort((a, b) => a.position - b.position)
        .map((section) => (
          <Col key={section.id}>
            <Row
              backgroundColor={selectedSectionId === section.id ? '#2D2D34' : '#ECEEF3'}
              hoverBackgroundColor={selectedSectionId === section.id ? '#2D2D34' : '#DADCE0'}
              justifyContent="space-between"
              alignItems="center"
              cursor="pointer"
              boxSizing="border-box"
              padding="16px 24px"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              transform="translateX(0)"
              hoverTransform="translateX(4px)"
              {...(!tablet && {
                padding: '12px 16px',
                hoverTransform: 'translateX(2px)',
              })}
              props={{
                onClick: () => {
                  toggleSection(section.id)
                  if (section.subSections?.length === 0) {
                    handleSectionClick(section.id)
                  }
                },
              }}
            >
              <Block
                fontWeight={400}
                lineHeight="24px"
                color={selectedSectionId === section.id ? '#FFFFFF' : '#000000'}
                fontSize="18px"
                transition="all 0.3s ease"
                {...(!tablet && {
                  fontSize: '16px',
                })}
              >
                {section.title}
              </Block>
              {section.subSections?.length !== 0 && (
                <motion.div animate={{rotate: openSections[section.id] ? 180 : 0}} transition={{duration: 0.3, ease: 'easeInOut'}}>
                  <DownArrowIcon width={8} height={8} color={selectedSectionId === section.id ? '#FFFFFF' : '#000000'} />
                </motion.div>
              )}
            </Row>

            <motion.div
              initial={false}
              animate={{
                height: openSections[section.id] ? 'auto' : 0,
                opacity: openSections[section.id] ? 1 : 0,
              }}
              transition={{
                duration: 0.4,
                ease: 'easeInOut',
              }}
              style={{overflow: 'hidden'}}
            >
              <Col>
                {section.subSections
                  ?.sort((a, b) => a.position - b.position)
                  .map((subSection: XpertKnowledgeSubSectionDto, index) => (
                    <Block
                      key={subSection.id}
                      cursor="pointer"
                      fontWeight={400}
                      lineHeight="21px"
                      backgroundColor={selectedSubSectionId === subSection.id ? '#2D2D34' : '#F7F8F9'}
                      hoverBackgroundColor={selectedSubSectionId === subSection.id ? '#2D2D34' : '#ECEEF3'}
                      color={selectedSubSectionId === subSection.id ? '#FFFFFF' : '#000000'}
                      borderBottom="1px solid #DADCE0"
                      boxSizing="border-box"
                      padding="10px 28px"
                      fontSize="14px"
                      transition="all 0.3s ease"
                      transform="translateX(0)"
                      hoverTransform="translateX(6px)"
                      {...(!tablet && {
                        padding: '8px 20px',
                        fontSize: '13px',
                        lineHeight: '20px',
                        hoverTransform: 'translateX(4px)',
                      })}
                      props={{
                        onClick: () => {
                          handleSectionClick(section.id, subSection.id)
                        },
                      }}
                    >
                      {(section.subSections?.length ?? 0) > 1 ? `${index + 1}. ${subSection.title}` : subSection.title}
                    </Block>
                  )) || (
                  <Block
                    color="#888"
                    fontSize="14px"
                    padding="10px 28px"
                    transition="all 0.3s ease"
                    {...(!tablet && {
                      fontSize: '13px',
                      padding: '8px 20px',
                    })}
                  >
                    No sub-sections
                  </Block>
                )}
              </Col>
            </motion.div>
          </Col>
        ))}
    </Col>
  )
}

export default XpertKnowledgeTutorialSidebar
